import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";
import { MobileCategoryBar } from "@/components/MobileCategoryBar";
import { products, categories } from "@/data/products";

const catalogSearchSchema = z.object({
  category: fallback(z.string(), "all").default("all"),
});

export const Route = createFileRoute("/catalog")({
  validateSearch: zodValidator(catalogSearchSchema),
  head: ({ match }) => {
    const search = (match.search ?? {}) as { category?: string };
    const knownIds = new Set(categories.map((c) => c.id));
    const cat = search.category && knownIds.has(search.category) ? search.category : "all";
    const catLabel = cat === "all" ? null : categories.find((c) => c.id === cat)?.label ?? null;
    const url =
      cat === "all" ? "https://shopb.life/catalog" : `https://shopb.life/catalog?category=${cat}`;
    const title = catLabel ? `${catLabel} — angle-shop.com` : "Collection — angle-shop.com";
    const description = catLabel
      ? `Découvrez notre sélection ${catLabel.toLowerCase()} : pièces de marque, livraison offerte dès 59€.`
      : "Parcourez notre collection complète de vêtements et chaussures de marque. T-shirts, hoodies, sneakers et plus.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: CatalogPage,
});

function CatalogPage() {
  const { category: activeCategory } = Route.useSearch();
  const navigate = useNavigate({ from: "/catalog" });
  const filtered = activeCategory === "all" ? products : products.filter((p) => p.category === activeCategory);

  const setActiveCategory = (id: string) => {
    navigate({ search: { category: id } });
  };

  return (
    <main className="min-h-screen bg-background">
      <Header />
      <MobileCategoryBar activeCategory={activeCategory} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground tracking-tight">Catalog</h1>
          <p className="text-muted-foreground mt-2">All our connected accessories for your vehicle</p>
        </motion.div>

        <div className="hidden md:flex gap-2 mt-8 overflow-x-auto pb-2 scrollbar-hide">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeCategory === cat.id
                  ? "bg-electric text-electric-foreground shadow-electric"
                  : "bg-card text-muted-foreground hover:bg-accent border border-border"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-8">
          {filtered.map((product, i) => (
            <ProductCard key={product.id} product={product} index={i} />
          ))}
        </div>
      </div>
      <Footer />
    </main>
  );
}
